import { Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { AddressSuggestion, useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { DynamicFieldAreaBaseProps, getInitialDynamicFieldValues, useDynamicFields } from '../../utils/dynamicFields';
import getAvailableDeliveryTypes from '../../utils/getAvailableDeliveryTypes';
import { getAddressForSubmission, getInitialAddressValues } from '../../utils/streetHouseNumber';
import { pushErrorTrackingEvent } from '../../utils/tracking';
import MainHeadline from '../atoms/MainHeadline';
import AddressForm from '../molecules/AddressForm';
import AddressSuggestions from '../molecules/AddressSuggestions';
import CarrierOptions from '../molecules/CarrierOptions';
import CollectionPoints from '../molecules/CollectionPoints';
import DeliveryOptions from '../molecules/DeliveryOptions';
import Navigation from '../molecules/Navigation';
import { showDefaultError, showNotification } from '../molecules/Notifications';

const ShippingStep = () => {
  const { formatMessage } = useIntl();
  const { checkoutState, setShippingAddress, setCollectionPoint } = useCheckoutState();
  const navigate = useNavigate();
  const { submitDynamicFieldArea, getCarrierCustomerId } = useDynamicFields();
  const [isLoading, setLoading] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>();

  const {
    customer,
    orderOptions: { shipping: shippingOptions },
    orderState: { shipping },
  } = checkoutState;

  if (!customer) {
    return <Navigate to="/auth" replace />;
  }

  const selectedCarrier = shipping?.selected?.[0];

  const { carrierKey, carrierGroupKey, shippingOptionKey } = selectedCarrier ?? {
    carrierKey: '',
    carrierGroupKey: '',
    shippingOptionKey: '',
  };

  const isCollectionPoint = !!checkoutState.orderState.addresses?.shipping?.collectionPoint;

  const initialDeliveryType = !shippingOptions
    ? 'homeDelivery'
    : isCollectionPoint
    ? 'collectionPoint'
    : getAvailableDeliveryTypes(shippingOptions)[0];

  const initialValues = {
    deliveryType: initialDeliveryType,
    carrier: { carrierKey, carrierGroupKey, shippingOptionKey },
    shippingAddress: {
      ...(checkoutState.orderState.addresses?.shipping && !isCollectionPoint
        ? getInitialAddressValues(checkoutState.orderState.addresses.shipping)
        : {
            country: config.availableCountries.shipping[0].iso2Code,
            gender: customer?.data.gender ?? 'f',
            title: customer?.data.title ?? '',
            firstName: customer?.data.firstName ?? '',
            lastName: customer?.data.lastName ?? '',
            streetHouseNumber: '',
            street: '',
            houseNumber: '',
            additional: '',
            zipCode: '',
            city: '',
            state: '',
          }),
      ...getInitialDynamicFieldValues(checkoutState, 'shippingAddress'),
    },
    collectionPoint: {
      id: '',
      type: {
        carrierGroupKey: '',
        carrierKey: '',
        shippingOptionKey: '',
        collectionPointType: '',
      },
      additionalFields: getInitialDynamicFieldValues(checkoutState, 'collectionPoint'),
    },
  };

  const statusCodeI18nKeys = {
    405: 'shipping.notifications.deactivated',
    412: 'shipping.notifications.validationError',
  };

  const statusCodeTrackingEventAction = {
    405: 'general form',
    412: 'input errors',
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      if (values.deliveryType === 'homeDelivery') {
        const _addressSuggestions = await setShippingAddress(getAddressForSubmission(values.shippingAddress));
        if (_addressSuggestions) {
          setAddressSuggestions(_addressSuggestions);
          return;
        }
        await submitDynamicFieldArea(
          {
            area: 'shippingAddress',
            context: 'shippingAddress',
            conditionValues: {
              carrierGroupKey: values.carrier?.carrierGroupKey,
              shippingOptionKey: values.carrier?.shippingOptionKey,
              shippingCountry: values.shippingAddress.country,
            },
          },
          values
        );
      } else if (
        (['storePickup', 'collectionPoint'] as (string | undefined)[]).includes(values.deliveryType) &&
        !!values.collectionPoint.id
      ) {
        const dynamicFieldArea: DynamicFieldAreaBaseProps = {
          area: 'collectionPoint',
          context: 'collectionPoint.additionalFields',
          conditionValues: {
            collectionPointType: values.collectionPoint.type.collectionPointType,
            carrierGroupKey: values.collectionPoint.type.carrierGroupKey,
            shippingOptionKey: values.collectionPoint.type.shippingOptionKey,
          },
        };
        const carrierCustomerId = getCarrierCustomerId(dynamicFieldArea, values);
        await setCollectionPoint({
          specification: {
            collectionPointId: values.collectionPoint.id,
            ...(carrierCustomerId && { collectionPointCustomerNumber: carrierCustomerId }),
            customer: { firstName: values.shippingAddress.firstName, lastName: values.shippingAddress.lastName },
          },
          type: values.collectionPoint.type,
        });
        await submitDynamicFieldArea(dynamicFieldArea, values);
      }
      navigate('/payment');
    } catch (e: any) {
      const error = statusCodeI18nKeys[e.statusCode];
      if (error) {
        const message = formatMessage({ id: error });
        pushErrorTrackingEvent(statusCodeTrackingEventAction[e.statusCode], 'error', message);

        showNotification({
          level: 'error',
          text: message,
          autoClose: true,
          closable: true,
          singleInstance: true,
          reference: 'shipping',
        });
      } else {
        console.error(e);
        showDefaultError();
      }
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ values }) => (
        <>
          {addressSuggestions && (
            <AddressSuggestions
              addresses={addressSuggestions}
              context="shippingAddress"
              onClose={() => setAddressSuggestions(undefined)}
            />
          )}
          <div className="form shipping-form">
            <MainHeadline>
              <FormattedMessage id="shipping.headline" />
            </MainHeadline>
            <DeliveryOptions />
            {['collectionPoint', 'storePickup'].includes(values.deliveryType!) ? (
              <CollectionPoints />
            ) : (
              <>
                {config.carrierOptions.position === 'shippingTop' && <CarrierOptions setLoading={setLoading} />}
                <AddressForm
                  headline={<FormattedMessage id="address.shippingAddress" />}
                  context="shippingAddress"
                  availableCountries={config.availableCountries.shipping.map(({ iso2Code }) => iso2Code)}
                />
                {config.carrierOptions.position === 'shippingBottom' && <CarrierOptions setLoading={setLoading} />}
              </>
            )}
            <Navigation
              nextStepLabel={<FormattedMessage id="shipping.continueToPayment" />}
              isDisabled={
                !values.deliveryType ||
                (['collectionPoint', 'storePickup'].includes(values.deliveryType) && !values.collectionPoint.id)
              }
              isLoading={isLoading}
            />
            {DEBUG && <pre style={{ marginTop: 60 }}>{JSON.stringify(values, null, 2)}</pre>}
          </div>
        </>
      )}
    </Formik>
  );
};

export default ShippingStep;
